/**
 * A2 (vague « Fluidité », constats 4, 5, 8, 9) — formulaire unique « Planifier une réunion ».
 *
 * Utilisé par Suivi chantier › Réunions et par le raccourci de la Vue d'ensemble (qui
 * ouvre directement ce formulaire, sans changer d'onglet).
 *   - lieu pré-rempli : « Sur site — adresse du chantier » ;
 *   - participants : équipe de maîtrise d'œuvre et entreprises des lots, chaque
 *     entreprise une seule fois (avec son lot) ;
 *   - « Envoyer la convocation maintenant » : la convocation (date, heure, lieu, ordre
 *     du jour, Présent / Absent) part dès la création, sur demande explicite.
 * API : POST /chantiers/{id}/evenements, puis POST /evenements/{eid}/convoquer.
 */
import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { X } from '@phosphor-icons/react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import ContactSearch from '../ui/ContactSearch';
import { libelleRole } from '../../lib/libelles';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const lieuParDefaut = (chantier) => {
  const adresse = (chantier?.adresse || chantier?.address || '').trim();
  return adresse ? `Sur site — ${adresse}` : 'Sur site';
};

const formulaireVide = (date = '', lieu = '') => ({
  titre: 'Réunion de chantier', date_debut: date, lieu, type: 'reunion_chantier',
  recurrence: '', invites: [], ordre_du_jour: [''],
});

const cle = (c) => (c.email || c.nom || '').trim().toLowerCase();

export default function PlanifierReunionDialog({ chantierId, open, onClose, onCreated, dateInitiale = '' }) {
  const [form, setForm] = useState(formulaireVide());
  const [equipe, setEquipe] = useState([]);
  const [entreprises, setEntreprises] = useState([]);
  const [lieuDefaut, setLieuDefaut] = useState('Sur site');
  const [convoquer, setConvoquer] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!open) return;
    let alive = true;
    (async () => {
      const [ch, moe, marches] = await Promise.all([
        axios.get(`${API_URL}/api/chantiers/${chantierId}`, { withCredentials: true }).catch(() => ({ data: {} })),
        axios.get(`${API_URL}/api/projets/${chantierId}/moe`, { withCredentials: true }).catch(() => ({ data: [] })),
        axios.get(`${API_URL}/api/chantiers/${chantierId}/marches`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      if (!alive) return;
      const lieu = lieuParDefaut(ch.data);
      setLieuDefaut(lieu);
      setForm(formulaireVide(dateInitiale, lieu));
      setConvoquer(false);
      const ents = (Array.isArray(marches.data) ? marches.data : [])
        .filter((l) => l.entreprise_nom)
        .map((l) => ({ nom: l.entreprise_nom, email: l.entreprise_email || '', role: `Lot ${l.code}` }))
        .filter((e, i, arr) => arr.findIndex((x) => x.nom === e.nom) === i);
      setEntreprises(ents);
      // constat 9 — une entreprise n'apparaît pas aussi dans l'équipe de maîtrise d'œuvre
      const nomsEnt = new Set(ents.map((e) => e.nom.trim().toLowerCase()));
      const mailsEnt = new Set(ents.map((e) => (e.email || '').trim().toLowerCase()).filter(Boolean));
      setEquipe((Array.isArray(moe.data) ? moe.data : []).filter((u) => (
        u.role !== 'entreprise'
        && !nomsEnt.has((u.name || '').trim().toLowerCase())
        && !mailsEnt.has((u.email || '').trim().toLowerCase())
      )));
    })();
    return () => { alive = false; };
  }, [open, chantierId, dateInitiale]);

  const estInvite = (c) => form.invites.some((p) => cle(p) === cle(c));
  const basculer = (contact) => {
    if (estInvite(contact)) {
      setForm({ ...form, invites: form.invites.filter((p) => cle(p) !== cle(contact)) });
    } else {
      setForm({ ...form, invites: [...form.invites, { ...contact, presence: 'obligatoire', reponse: 'en_attente' }] });
    }
  };

  const planifier = async () => {
    if (!form.date_debut) { toast.error('Indiquez la date et l\'heure de la réunion.'); return; }
    setSaving(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/chantiers/${chantierId}/evenements`, {
        ...form, lieu: (form.lieu || '').trim() || lieuDefaut, ordre_du_jour: form.ordre_du_jour.filter((o) => o.trim()),
      }, { withCredentials: true });
      if (convoquer && data?.id) {
        const res = await axios.post(`${API_URL}/api/chantiers/${chantierId}/evenements/${data.id}/convoquer`, {}, { withCredentials: true });
        toast.success(`Réunion planifiée — ${res.data?.message || 'convocation envoyée'}`);
      } else {
        toast.success('Réunion planifiée' + (form.recurrence ? ` (récurrence ${form.recurrence})` : ''));
      }
      onCreated && onCreated(data?.id);
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'La réunion n\'a pas pu être planifiée.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="planifier-reunion-dialog">
        <DialogHeader><DialogTitle style={{ fontFamily: 'DM Serif Display, serif' }}>Planifier une réunion</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div><Label>Titre</Label><Input value={form.titre} onChange={(e) => setForm({ ...form, titre: e.target.value })} className="rounded-none" /></div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <Label>Date et heure</Label>
              <Input type="datetime-local" value={form.date_debut} onChange={(e) => setForm({ ...form, date_debut: e.target.value })} className="rounded-none" data-testid="planifier-reunion-date" />
            </div>
            <div>
              <Label>Lieu</Label>
              <Input value={form.lieu} onChange={(e) => setForm({ ...form, lieu: e.target.value })} className="rounded-none" data-testid="planifier-reunion-lieu" />
            </div>
          </div>
          <div>
            <Label>Récurrence</Label>
            <Select value={form.recurrence || 'none'} onValueChange={(v) => setForm({ ...form, recurrence: v === 'none' ? '' : v })}>
              <SelectTrigger className="rounded-none"><SelectValue placeholder="Pas de récurrence" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Pas de récurrence</SelectItem>
                <SelectItem value="hebdomadaire">Chaque semaine</SelectItem>
                <SelectItem value="bimensuel">Toutes les deux semaines</SelectItem>
                <SelectItem value="mensuel">Chaque mois</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Participants à convoquer ({form.invites.length})</Label>
            <div className="mt-1.5 mb-2">
              <ContactSearch
                sources={['equipe', 'marches', 'users']}
                chantierId={chantierId}
                placeholder="Rechercher un participant (équipe, entreprise, contact…)"
                onSelect={(c) => { if (c) basculer({ nom: c.name, email: c.email || '', telephone: c.telephone || '', role: c.detail || c.source }); }}
                testId="reunion-invite-search"
              />
            </div>
            {form.invites.length > 0 && (
              <div className="mb-2 flex flex-wrap gap-1.5">
                {form.invites.map((inv, i) => (
                  <span key={`${cle(inv)}-${i}`} className="inline-flex items-center gap-1 px-2 py-0.5 bg-[#FDF6EC] border border-[#C4773B] text-xs text-[#6B4A24]">
                    {inv.nom}
                    <button type="button" onClick={() => basculer(inv)} className="hover:text-[#8B3A3A]" aria-label={`Retirer ${inv.nom}`} data-testid={`reunion-invite-remove-${i}`}>
                      <X size={10} />
                    </button>
                  </span>
                ))}
              </div>
            )}
            {equipe.length > 0 && (
              <div className="mt-2"><p className="text-[10px] font-semibold text-[#6B6B6B] uppercase mb-1">Équipe de maîtrise d'œuvre</p>
                {equipe.map((u) => (
                  <label key={u.id || u._id || u.email} className="flex items-center gap-2 py-1.5 px-2 hover:bg-[#F5F5F3] cursor-pointer text-sm">
                    <input type="checkbox" checked={estInvite({ nom: u.name, email: u.email })} onChange={() => basculer({ nom: u.name, email: u.email, telephone: u.telephone || '', role: u.role })} className="accent-[#C75B39]" />
                    <span>{u.name}</span><span className="text-xs text-[#9A9A9A] ml-auto">{libelleRole(u.role)}</span>
                  </label>
                ))}
              </div>
            )}
            {entreprises.length > 0 && (
              <div className="mt-2"><p className="text-[10px] font-semibold text-[#6B6B6B] uppercase mb-1">Entreprises</p>
                {entreprises.map((e) => (
                  <label key={e.nom} className="flex items-center gap-2 py-1.5 px-2 hover:bg-[#F5F5F3] cursor-pointer text-sm">
                    <input type="checkbox" checked={estInvite(e)} onChange={() => basculer(e)} className="accent-[#C75B39]" />
                    <span>{e.nom}</span><span className="text-xs text-[#9A9A9A] ml-auto">{e.role}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          <div>
            <Label>Ordre du jour</Label>
            {form.ordre_du_jour.map((item, i) => (
              <div key={i} className="flex gap-2 mt-1">
                <Input value={item} onChange={(e) => { const odj = [...form.ordre_du_jour]; odj[i] = e.target.value; setForm({ ...form, ordre_du_jour: odj }); }} className="rounded-none flex-1" placeholder={`Point ${i + 1}`} />
                {i > 0 && <Button variant="ghost" size="sm" className="px-2" onClick={() => setForm({ ...form, ordre_du_jour: form.ordre_du_jour.filter((_, j) => j !== i) })} aria-label="Retirer ce point"><X size={14} /></Button>}
              </div>
            ))}
            <Button variant="ghost" size="sm" className="mt-1 text-xs text-[#C75B39]" onClick={() => setForm({ ...form, ordre_du_jour: [...form.ordre_du_jour, ''] })}>+ Ajouter un point</Button>
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer" data-testid="planifier-reunion-convoquer">
            <input type="checkbox" checked={convoquer} onChange={(e) => setConvoquer(e.target.checked)} className="accent-[#C75B39]" disabled={form.invites.length === 0} />
            Envoyer la convocation maintenant ({form.invites.filter((i) => i.email).length} participant{form.invites.filter((i) => i.email).length > 1 ? 's' : ''} avec e-mail)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={onClose} className="rounded-none">Annuler</Button>
            <Button onClick={planifier} disabled={saving} className="bg-[#2C2C2C] hover:bg-[#4A4A4A] rounded-none" data-testid="create-reunion-submit">
              {saving ? 'Planification…' : 'Planifier'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
